import { FormState } from '../types'

export type Status = 'Complete' | 'Missing Info' | 'Needs Review' | 'Blocked'

export interface ChecklistItem {
  id: string
  label: string
  status: Status
  explanation: string
  sectionId: string
}

export interface ValidationResult {
  sections: Record<string, Status>
  checklist: ChecklistItem[]
  canSubmit: boolean
  hardStops: ChecklistItem[]
}

export function validateForm(state: FormState): ValidationResult {
  const checklist: ChecklistItem[] = []
  const hardStops: ChecklistItem[] = []
  const sections: Record<string, Status> = {
    contacts: 'Complete',
    property: 'Complete',
    product: 'Complete',
    disclosure: 'Complete',
    notes: 'Complete',
  }

  const addChecklist = (
    id: string,
    label: string,
    status: Status,
    explanation: string,
    sectionId: string,
  ) => {
    checklist.push({ id, label, status, explanation, sectionId })
  }

  // ============================================================
  // AUTOMATIC SYSTEM HARD STOPS
  // These are evaluated automatically by the system. If any fail,
  // they block submission outright (no manual section to "fix").
  // ============================================================
  const addHardStop = (
    id: string,
    label: string,
    passed: boolean,
    failExplanation: string,
    passExplanation: string,
    sectionId: string,
  ) => {
    const item: ChecklistItem = {
      id,
      label,
      status: passed ? 'Complete' : 'Blocked',
      explanation: passed ? passExplanation : failExplanation,
      sectionId,
    }
    checklist.push(item)
    hardStops.push(item)
  }

  // Disclosure request date is not already populated
  addHardStop(
    'req-date',
    'Disclosure Request Date',
    !state.disclosureRequestDatePopulated,
    'A disclosure request date is already populated for this loan.',
    'Verified — no existing disclosure request date.',
    'property',
  )

  // LO is licensed in the property state
  addHardStop(
    'lo-license',
    'LO Licensed in State',
    state.loLicensedInState,
    `Loan officer is not licensed in ${state.propertyState || 'the property state'}.`,
    'Loan officer is licensed in the property state.',
    'property',
  )

  // AUS findings have been certified
  addHardStop(
    'aus-findings',
    'AUS Findings Certified',
    state.ausFindingsCertified,
    'AUS findings have not been certified.',
    'AUS findings are certified.',
    'product',
  )

  // LE and pricing review preference has been selected
  addHardStop(
    'le-pricing',
    'LE & Pricing Review',
    state.lePricingReviewPreferenceSelected,
    'LE and pricing review preference has not been selected.',
    'LE and pricing review preference is selected.',
    'disclosure',
  )

  // ============================================================
  // CONTACTS (borrower + agents + attorney + HOA consolidated)
  // ============================================================

  // Borrower file contacts complete
  if (
    !state.borrowerFirstName ||
    !state.borrowerLastName ||
    !state.borrowerEmail ||
    !state.borrowerMobile
  ) {
    addChecklist(
      'file-contacts',
      'Borrower File Contacts',
      'Missing Info',
      'Borrower name, email, or mobile is missing.',
      'contacts',
    )
    sections.contacts = 'Missing Info'
  } else {
    addChecklist(
      'file-contacts',
      'Borrower File Contacts',
      'Complete',
      'Borrower contact info is complete.',
      'contacts',
    )
  }

  // Preferred borrower contact complete
  if (!state.preferredContactLanguage || !state.preferredContactMethod) {
    addChecklist(
      'pref-contact',
      'Preferred Contact',
      'Missing Info',
      'Preferred contact language or method is missing.',
      'contacts',
    )
    if (sections.contacts !== 'Blocked') sections.contacts = 'Missing Info'
  } else {
    addChecklist(
      'pref-contact',
      'Preferred Contact',
      'Complete',
      'Preferred contact info is complete.',
      'contacts',
    )
  }

  // Purchase-only contact requirements
  if (state.loanPurpose === 'Purchase') {
    // Required purchase fields (agents + commitment due)
    if (
      !state.buyerAgent ||
      !state.listingAgent ||
      !state.mortgageCommitmentDue
    ) {
      addChecklist(
        'purchase-fields',
        'Purchase Agents & Commitment',
        'Missing Info',
        'Buyer/Listing agent or Mortgage Commitment Due is missing.',
        'contacts',
      )
      if (sections.contacts !== 'Blocked') sections.contacts = 'Missing Info'
    } else {
      addChecklist(
        'purchase-fields',
        'Purchase Agents & Commitment',
        'Complete',
        'Required purchase agents and commitment date are present.',
        'contacts',
      )
    }

    // Attorney info for NY, CT, TX
    if (['NY', 'CT', 'TX'].includes(state.propertyState)) {
      if (!state.buyerAttorney || !state.sellerAttorney) {
        addChecklist(
          'attorney-info',
          'Attorney Info',
          'Blocked',
          `Buyer's and Seller's attorney are required for ${state.propertyState}.`,
          'contacts',
        )
        sections.contacts = 'Blocked'
      } else {
        addChecklist(
          'attorney-info',
          'Attorney Info',
          'Complete',
          'Attorney info is complete.',
          'contacts',
        )
      }
    }
  }

  // Condo/Co-op/HOA contact required by property type
  const hoaPropertyTypes = [
    'Condo',
    'Co-op',
    'High-Rise Condo',
    'Detached Condo',
  ]
  if (hoaPropertyTypes.includes(state.propertyType)) {
    if (!state.hoaContact) {
      addChecklist(
        'hoa-contact',
        'HOA Contact',
        'Blocked',
        `HOA contact is required for ${state.propertyType}.`,
        'contacts',
      )
      sections.contacts = 'Blocked'
    } else {
      addChecklist(
        'hoa-contact',
        'HOA Contact',
        'Complete',
        'HOA contact is complete.',
        'contacts',
      )
    }
  }

  // ============================================================
  // PROPERTY & TRANSACTION
  // ============================================================

  // Appraisal company (now a contact lookup in Required Contacts)
  if (!state.appraisalCompanyName) {
    addChecklist(
      'appraisal-co',
      'Appraisal Company',
      'Missing Info',
      'Appraisal company name is missing.',
      'contacts',
    )
    if (sections.contacts !== 'Blocked') sections.contacts = 'Missing Info'
  } else {
    addChecklist(
      'appraisal-co',
      'Appraisal Company',
      'Complete',
      'Appraisal company is provided.',
      'contacts',
    )
  }

  // Property street not TBD
  if (state.propertyStreet.toUpperCase() === 'TBD' || !state.propertyStreet) {
    addChecklist(
      'prop-street',
      'Property Street',
      'Blocked',
      'Property street cannot be TBD or empty.',
      'property',
    )
    sections.property = 'Blocked'
  } else {
    addChecklist(
      'prop-street',
      'Property Street',
      'Complete',
      'Valid property street.',
      'property',
    )
  }

  // Madison ECD present + certified
  if (!state.madisonECD) {
    addChecklist(
      'madison-ecd',
      'Madison ECD',
      'Blocked',
      'Madison ECD is missing.',
      'property',
    )
    sections.property = 'Blocked'
  } else if (!state.madisonECDCertified) {
    addChecklist(
      'madison-ecd',
      'Madison ECD',
      'Needs Review',
      'Madison ECD needs certification.',
      'property',
    )
    if (sections.property !== 'Blocked' && sections.property !== 'Missing Info')
      sections.property = 'Needs Review'
  } else {
    addChecklist(
      'madison-ecd',
      'Madison ECD',
      'Complete',
      'Madison ECD is complete and certified.',
      'property',
    )
  }

  // ============================================================
  // LOAN PRODUCT
  // ============================================================
  if (state.qmType === 'Non-QM' && !state.nonQmProductType) {
    sections.product = 'Missing Info'
  }
  if (!state.processingType) {
    if (sections.product !== 'Blocked') sections.product = 'Missing Info'
  }

  // ============================================================
  // DISCLOSURE DETAILS
  // ============================================================
  if (state.lockPeriod === 'Custom' && !state.customLockPeriod) {
    sections.disclosure = 'Missing Info'
  }
  if (
    state.buyerPayingSellerTransferTax === 'Yes' &&
    !state.transferTaxAmount
  ) {
    sections.disclosure = 'Missing Info'
  }
  if (state.prePaymentPenalty === 'Yes' && !state.prePaymentPenaltyDuration) {
    sections.disclosure = 'Missing Info'
  }
  if (
    state.buyerPayingRealtorCommission === 'Yes' &&
    !state.realtorCommission
  ) {
    sections.disclosure = 'Missing Info'
  }

  const canSubmit =
    !checklist.some(
      (item) => item.status === 'Blocked' || item.status === 'Missing Info',
    ) &&
    !Object.values(sections).some(
      (status) => status === 'Blocked' || status === 'Missing Info',
    )

  return { sections, checklist, canSubmit, hardStops }
}
